import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";
import {
  ArrowRight,
  ShieldCheck,
  Database,
  Layers,
  Globe2,
  BarChart3,
  MapPin,
  CheckCircle2,
} from "lucide-react";

export default function Home() {
  const { t, dict } = useI18n();
  const metricsItems = dict.home.metrics.items;
  const whyCards = dict.home.why.cards;
  const howSteps = dict.home.how.steps;
  const dashFeatures = dict.home.dashboard.features;
  const transparencyPillars = dict.home.transparency.pillars;
  const projects = dict.home.projects.items;

  const whyIcons = [Database, Globe2, Layers, ShieldCheck];
  const projectImages = [
    "/images/mining.jpg",
    "/images/solar-farm.jpg",
    "/images/wind-turbines.jpg",
  ];

  return (
    <main data-testid="page-home" className="bg-[#F8FAFC]">
      {/* HERO */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
              {t("home.hero.eyebrow")}
            </p>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A192F] leading-[1.05]">
              {t("home.hero.title")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#334155] leading-relaxed">
              {t("home.hero.lead")}
            </p>
            <p className="mt-4 text-base text-[#334155] leading-relaxed">
              {t("home.hero.body")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="hero-primary-cta"
                href="#newsletter"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#059669] px-6 py-3 text-sm font-semibold text-white hover:bg-[#047857]"
              >
                {t("home.hero.primaryCta")}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                data-testid="hero-secondary-cta"
                href="#how"
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-[#0A192F]/20 px-6 py-3 text-sm font-semibold text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
              >
                {t("home.hero.secondaryCta")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative border border-[#0A192F]/10">
              <img
                src="/images/hero-lithium.jpg"
                alt={t("home.hero.imageAlt")}
                className="w-full h-[420px] object-cover"
                loading="eager"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-[#0A192F] px-5 py-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  Allier · Beauvoir · 2026
                </p>
                <p className="mt-1 text-sm font-semibold text-white">
                  {t("home.projects.items.0.text").split(",")[0]}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="bg-[#0A192F] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white max-w-2xl">
              {t("home.metrics.title")}
            </h2>
            <p className="max-w-md text-sm text-[#94A3B8]">
              {t("home.metrics.subtitle")}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {metricsItems.map((m, i) => (
              <div
                key={i}
                data-testid={`metric-${i}`}
                className="bg-[#0A192F] p-6 lg:p-8"
              >
                <p className="font-heading text-3xl lg:text-4xl font-bold text-[#10B981]">
                  {m.value}
                </p>
                <p className="mt-3 text-sm text-white leading-relaxed">
                  {m.label}
                </p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
                  {m.source}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
              {t("home.why.eyebrow")}
            </p>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
              {t("home.why.title")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {whyCards.map((c, i) => {
              const Icon = whyIcons[i] || Database;
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="bg-white p-8 lg:p-10"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-[#ECFDF5] text-[#047857]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-[#0A192F]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#334155]">
                    {c.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
                {t("home.how.eyebrow")}
              </p>
              <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
                {t("home.how.title")}
              </h2>
              <p className="mt-5 text-base text-[#334155] leading-relaxed">
                {t("home.how.intro")}
              </p>
              <div className="mt-8 border border-[#0A192F]/10">
                <img
                  src="/images/dashboard.jpg"
                  alt={t("home.dashboard.imageAlt")}
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-4">
                {howSteps.map((s, i) => (
                  <li
                    key={i}
                    data-testid={`how-step-${i}`}
                    className="flex gap-5 bg-white border border-[#E2E8F0] p-6 lg:p-8"
                  >
                    <span className="font-mono text-xl font-bold text-[#059669] shrink-0">
                      {s.number}
                    </span>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-[#0A192F]">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#334155]">
                        {s.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* DASHBOARD */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="border border-[#0A192F]/10">
              <img
                src="/images/france-map.jpg"
                alt={t("home.dashboard.imageAlt")}
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
              {t("home.dashboard.eyebrow")}
            </p>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
              {t("home.dashboard.title")}
            </h2>
            <p className="mt-5 text-base text-[#334155] leading-relaxed">
              {t("home.dashboard.text")}
            </p>
            <ul className="mt-8 space-y-3">
              {dashFeatures.map((f, i) => (
                <li
                  key={i}
                  data-testid={`dashboard-feature-${i}`}
                  className="flex items-start gap-3 border-l-2 border-[#059669] pl-4 py-1"
                >
                  <BarChart3 className="h-4 w-4 mt-1 text-[#047857] shrink-0" />
                  <span className="text-sm text-[#0A192F]">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section
        id="transparency"
        className="bg-[#0A192F] text-white border-b border-white/10"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-widest text-[#10B981]">
              {t("home.transparency.eyebrow")}
            </p>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("home.transparency.title")}
            </h2>
            <p className="mt-5 text-base text-[#CBD5E1] leading-relaxed">
              {t("home.transparency.lead")}
            </p>
            <p className="mt-3 text-base text-[#CBD5E1] leading-relaxed">
              {t("home.transparency.body")}
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {transparencyPillars.map((p, i) => (
              <div
                key={i}
                data-testid={`transparency-pillar-${i}`}
                className="bg-[#0A192F] p-6 lg:p-8"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#059669]">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#CBD5E1]">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
              {t("home.projects.eyebrow")}
            </p>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
              {t("home.projects.title")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <article
                key={i}
                data-testid={`project-card-${i}`}
                className="bg-white border border-[#E2E8F0] flex flex-col"
              >
                <img
                  src={projectImages[i] || "/images/industrial.jpg"}
                  alt={p.imageAlt}
                  className="w-full h-52 object-cover"
                />
                <div className="p-6 flex flex-col flex-1">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#059669]">
                    <MapPin className="h-3 w-3" />
                    {p.location}
                  </p>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-[#0A192F]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#334155] flex-1">
                    {p.text}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#059669]">
                {t("home.newsletter.eyebrow")}
              </p>
              <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
                {t("home.newsletter.title")}
              </h2>
              <p className="mt-5 text-base text-[#334155] leading-relaxed">
                {t("home.newsletter.text")}
              </p>
              <div className="mt-8 border border-[#0A192F]/10">
                <img
                  src="/images/analyst.jpg"
                  alt={t("home.dashboard.imageAlt")}
                  className="w-full h-[280px] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-6 bg-white border border-[#E2E8F0] p-8 lg:p-10">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
